import {
  allCategories,
  AllCategory,
  categories,
  Category,
  fileExports,
  HasName,
  hasName,
  KitchenClass,
  KitchenDeclaration,
  KitchenFileExport,
} from '../referenceData/theKitchenDocs';

export interface ComponentDocumentation {
  category: Category;
  component: HasName<KitchenDeclaration & KitchenFileExport>;
  props: HasName<KitchenClass & KitchenFileExport>;
  events: HasName<KitchenClass & KitchenFileExport>;
}

export function getComponentProps(component: ComponentDocumentation) {
  return (component.props.properties ?? []).concat(component.events.properties ?? []);
}

export type NamedExport = HasName<KitchenFileExport>;

export type LegacyProps = {
  [key: string]: {
    type: string;
    default: string;
    description: string;
  };
};

export type LegacyComponent = LegacyProps & {
  category: string;
  componentDescription: string;
  componentName: string;
  extends: string[];
};

class Documentation {
  readonly allCategories = allCategories;
  readonly categories = categories;
  private readonly namedExports: NamedExport[];

  readonly byName: Record<string, NamedExport> = {};
  readonly byComponent: Record<string, ComponentDocumentation> = {};
  readonly byCategory: Partial<Record<Category, NamedExport[]>> = {};
  readonly byAllCategory: Partial<Record<AllCategory, NamedExport[]>> = {};
  readonly byKind: Partial<Record<KitchenFileExport['kind'], NamedExport[]>> = {};

  constructor() {
    this.namedExports = fileExports.filter(hasName).filter((e) => !e.name.startsWith('_'));
    this.namedExports.forEach((e) => {
      const { category, kind } = e;
      // AllCategory
      if (!(category in this.byAllCategory)) {
        this.byAllCategory[category] = [];
      }
      this.byAllCategory[category]!.push(e);

      // Category
      if (isCategory(category)) {
        if (!(category in this.byCategory)) {
          this.byCategory[category] = [];
        }
        this.byCategory[category]!.push(e);
      }

      // Kind
      if (!(kind in this.byKind)) {
        this.byKind[kind] = [];
      }
      this.byKind[kind]!.push(e);

      // Name
      if (e.name in this.byName) {
        throw new Error(`Duplicate name export ${e.name}`);
      }
      this.byName[e.name] = e;
    });

    // Component
    (this.byKind.variable ?? [])
      .concat(this.byKind.function ?? [])
      .filter(hasName)
      .filter(hasCategory)
      .map((e) => {
        const [propsName, eventsName] = [`${e.name}Props`, `${e.name}Events`];

        if (propsName in this.byName && eventsName in this.byName) {
          const [props, events] = [this.byName[propsName], this.byName[eventsName]];
          if (props.kind === 'class' && events.kind === 'class') {
            return { category: e.category, component: e, props, events };
          }
        }
        return null;
      })
      .filter(isNotNullish)
      .forEach((e) => (this.byComponent[e.component.name] = e));
  }

  private _legacyComponents?: Record<string, LegacyComponent[]> = undefined;
  get legacyComponents() {
    if (this._legacyComponents) {
      return this._legacyComponents;
    }
    const components = Object.entries(this.byComponent)
      .sort(([, a], [, b]) => a.component.name.localeCompare(b.component.name))
      .map(([, componentData]) => {
        const { category, component, props, events } = componentData;

        const compatiblePropsAndEvents = (props.properties ?? []).concat(events.properties ?? []).reduce<LegacyProps>((acc, curr) => {
          acc[curr.name + (curr.required ? '' : '?')] = {
            type: curr.type ?? '',
            default: curr.default ?? '',
            description: curr.description ?? '',
          };
          return acc;
        }, {});
        return {
          category,
          componentDescription: component.description?.replace(/^Description: /, '') ?? '',
          componentName: component.name ?? '',
          extends: props.extends ? [props.extends] : [],
          ...compatiblePropsAndEvents,
        } as LegacyComponent;
      });
    this._legacyComponents = components.reduce<NonNullable<Documentation['_legacyComponents']>>((acc, curr) => {
      if (!(curr.category in acc)) {
        acc[curr.category] = [];
      }
      acc[curr.category].push(curr);
      return acc;
    }, {});

    return this._legacyComponents;
  }
}
export const documentation = new Documentation();

export function isNotNullish<T>(value: T): value is NonNullable<T> {
  return value != null;
}

export function isCategory(value: unknown): value is Category {
  return categories.includes(value as any);
}

export function isAllCategory(value: unknown): value is AllCategory {
  return allCategories.includes(value as any);
}

export function hasCategory<T extends { category: AllCategory }>(value: T): value is T & { category: Category } {
  return isCategory(value.category);
}

export function isComponent(value: KitchenDeclaration): boolean {
  return value.name !== undefined && value.name in documentation.byComponent;
}

export * from '../referenceData/theKitchenDocs';
